<template>
  <div class="bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 min-h-screen transition-colors duration-300">
    <Navbar />
    <article class="max-w-3xl mx-auto px-4 py-24">
      <header class="mb-8">
        <Link href="/articles" class="inline-flex items-center text-amber-600 dark:text-amber-400 hover:underline mb-4">
          <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
          </svg>
          All Articles
        </Link>
        <h1 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
          {{ post.title }}
          <span
            v-if="post.lang"
            class="ml-2 px-2 py-1 rounded text-xs font-bold uppercase"
            :class="post.lang === 'pt-BR' ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400'"
          >
            {{ post.lang === 'pt-BR' ? 'PT' : 'EN' }}
          </span>
        </h1>
        <div class="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-500">
          <time :datetime="post.date">{{ post.formatted_time }}</time>
          <span v-if="post.tags.length" class="flex gap-2">
            <span
              v-for="tag in post.tags"
              :key="tag"
              class="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 rounded-full text-xs"
            >
              {{ tag }}
            </span>
          </span>
        </div>
      </header>

      <div class="prose prose-lg dark:prose-invert max-w-none" v-html="post.html"></div>

      <!-- Share on LinkedIn -->
      <div class="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
        <p class="text-gray-600 dark:text-gray-400 mb-4 text-center">{{ t('shareArticle') }}</p>
        <div class="flex justify-center">
          <a
            :href="linkedinShareUrl"
            target="_blank"
            class="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0077B5] text-white rounded-lg font-medium hover:bg-[#005885] transition-all hover:-translate-y-0.5 shadow-lg shadow-blue-500/20"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
            {{ t('shareOnLinkedIn') }}
          </a>
        </div>
      </div>
    </article>
    <Footer />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { Link } from '@inertiajs/vue3'
import Navbar from '@/components/Navbar.vue'
import Footer from '@/components/Footer.vue'
import { useI18n } from '@/i18n'

const { t } = useI18n()

const props = defineProps({
  post: {
    type: Object,
    required: true
  }
})

const linkedinShareUrl = computed(() => {
  const url = window.location.href
  const text = `${props.post.title} - Paulo Vitor`
  return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
})
</script>

<style>
.prose h1 { font-size: 1.5rem; font-weight: 700; color: #111827; margin-bottom: 1rem; margin-top: 2rem; }
.prose h2 { font-size: 1.25rem; font-weight: 600; color: #111827; margin-bottom: 0.75rem; margin-top: 1.5rem; }
.prose h3 { font-size: 1.125rem; font-weight: 600; color: #111827; margin-bottom: 0.5rem; margin-top: 1rem; }
.prose p { margin-bottom: 1rem; line-height: 1.75; }
.prose ul { list-style-type: disc; list-style-position: inside; margin-bottom: 1rem; }
.prose ul li { margin-top: 0.25rem; }
.prose ol { list-style-type: decimal; list-style-position: inside; margin-bottom: 1rem; }
.prose ol li { margin-top: 0.25rem; }
.prose li { color: #374151; }
.prose a { color: #d97706; text-decoration: underline; }
.prose a:hover { color: #b45309; }
.prose code { background-color: #f3f4f6; padding: 0.125rem 0.375rem; border-radius: 0.25rem; font-size: 0.875rem; }
.prose pre { background-color: #111827; color: #f3f4f6; padding: 1rem; border-radius: 0.5rem; overflow-x: auto; margin-bottom: 1rem; }
.prose pre code { background-color: transparent; padding: 0; }
.prose blockquote { border-left: 4px solid #f59e0b; padding-left: 1rem; font-style: italic; color: #4b5563; margin: 1rem 0; }
.prose hr { margin: 2rem 0; border-color: #e5e7eb; }
.prose strong { font-weight: 600; color: #111827; }
.prose em { font-style: italic; }
.prose table { width: 100%; border-collapse: collapse; margin-bottom: 1rem; }
.prose th, .prose td { border: 1px solid #e5e7eb; padding: 0.5rem 0.75rem; text-align: left; }
.prose th { background-color: #f9fafb; font-weight: 600; }

/* Dark mode */
:root.dark .prose h1, :root.dark .prose h2, :root.dark .prose h3 { color: #f9fafb; }
:root.dark .prose strong { color: #f9fafb; }
:root.dark .prose li { color: #d1d5db; }
:root.dark .prose code { background-color: #1f2937; }
:root.dark .prose a { color: #fbbf24; }
:root.dark .prose a:hover { color: #f59e0b; }
:root.dark .prose blockquote { color: #9ca3af; }
:root.dark .prose hr { border-color: #374151; }
:root.dark .prose th { background-color: #1f2937; }
:root.dark .prose th, :root.dark .prose td { border-color: #374151; }
</style>
